/**
 * Data Hooks
 *
 * React hooks for fetching data from Supabase via Repository pattern.
 * All queries are automatically scoped by tenant via RLS policies.
 * All return data is validated with Zod schemas for runtime type safety.
 *
 * Repository Pattern (v2.9.0):
 * - InventoryRepository: Inventory insights, quality metrics, store financials
 * - AlertRepository: Alerts, risk patterns, global overview
 *
 * TanStack Query Migration (v3.0.0):
 * - useKPIs, useStoreFinancials, useInventoryQuality now use TanStack Query
 *
 * CQRS-Lite Architecture (v2.9.1):
 * - KPI queries now use pre-computed `view_dashboard_kpis` materialized view
 * - Financial data aggregated from `daily_store_stats` summary table
 * - Triggers maintain incremental updates on transaction inserts
 * - Dramatically reduced query latency by avoiding raw transaction scans
 *
 * Frontend Caching Strategy (v3.3.0) - STOP CLIENT-SIDE COMPUTE FATIGUE:
 * - ANALYTICAL data (KPIs, GMROI, trends): 5 minute staleTime
 *   - Binds directly to pre-computed materialized views
 *   - No reduce/map/filter operations for KPI math in components
 *   - Smaller payloads (pre-aggregated data)
 * - OPERATIONAL data (alerts, tasks): 30s-1min staleTime
 *   - Only real-time listeners on `alerts` table (critical events)
 *   - No listeners on high-velocity `transactions` table
 *
 * This strategy eliminates UI flickering by:
 * 1. Longer cache times prevent excessive refetches
 * 2. Pre-computed views return smaller, faster payloads
 * 3. Components bind directly to API response without transformation
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys, STALE_TIMES } from '@/services/queryClient';
import { api } from '@/services/api';
import { supabase } from '@/services/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { isSchemasCacheError } from '@/services/supabaseErrors';
import { InventoryRepository, AlertRepository } from '@/services/repositories';
import { fmtPct, fmtScore, fmtCompact } from '@/services/formatters';
import {
  Alert, Case, KPI, RiskPattern, StoreStatus, RootCauseData, TrendData,
  StoreZone, OperationalCompliance, InventoryInsight, Report, RiskTrendPoint,
  GlobalStats, InventoryQualityMetrics, Task, StoreFinancialSummary,
  ProcessError, ZoneProcessMetrics
} from '@/types';

// ── Local DB row types (mirrors .select() columns to avoid `any`) ──

/** Row shape from `purchase_orders` query in useOperationalKPIs */
interface PurchaseOrderRow {
  id: string;
  is_late: boolean;
  delay_hours: number | null;
  total_units_ordered: number;
  total_units_received: number;
  is_short_shipped: boolean;
  short_ship_percentage: number | null;
  order_date: string;
  actual_arrival: string | null;
}

/** Row shape from `alerts` query (resolved, for MTTR) */
interface AlertResolutionRow {
  id: string;
  status: string;
  created_at: string;
  resolved_at: string | null;
  metadata?: { resolution?: string };
}

/** Row shape from `tasks` query in useOperationalCompliance */
interface TaskRow {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  completed_at: string | null;
}

/** Row shape from `inventory` + `products` join in useInventoryInsights */
interface InventoryProductRow {
  product_id: string;
  system_qty: number;
  physical_qty: number;
  last_audit_date: string | null;
  products: {
    name: string;
    sku: string;
    category: string;
    unit_cost: number;
    unit_price: number;
  } | null;
}

/** Row shape from `inventory_quality_metrics` time series */
interface QualityMetricsRow {
  calculated_at: string;
  ira: number | null;
  phantom_rate: number | null;
  gmroi: number | null;
  doh: number | null;
  shrinkage_rate: number | null;
  [key: string]: string | number | null | undefined;
}

/** Row shape from `daily_store_stats` */
interface DailyStatRow {
  stat_date: string;
  total_revenue: number;
  total_cogs: number;
  gross_margin: number;
  shrinkage_value: number;
  units_sold: number;
}

/** Row shape from `transactions` with user join */
interface TransactionRow {
  id: string;
  created_at: string;
  type: string;
  quantity: number;
  total_amount: number | null;
  product_id: string;
  cashier_id: string | null;
  users?: { full_name: string } | null;
}

// Generic hook for async data fetching
const useAsyncData = <T, P>(fetcher: (params?: P) => Promise<T> | T, params?: P) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const paramsKey = useMemo(() => params ? JSON.stringify(params) : 'no-params', [params]);

  const refresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        const result = await fetcher(params);
        setData(result);
      } catch (err) {
        if (import.meta.env.DEV) console.error('Failed to fetch data:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetcher, paramsKey, refreshTrigger]);

  return { data, loading, error, refresh };
};

// ============================================
// CORE DATA HOOKS (Real Supabase Data)
// ============================================

/**
 * Fetch alerts using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 * Caching: 30 seconds staleTime (time-sensitive data)
 * @param tenantId - Required tenant UUID for security filtering
 * @param storeId - Optional store UUID to filter by
 */
export const useAlerts = (tenantId?: string, storeId?: string) => {
  return useQuery({
    queryKey: queryKeys.alerts(tenantId!, storeId),
    queryFn: () => AlertRepository.getAlerts(tenantId!, storeId),
    staleTime: STALE_TIMES.ALERTS,
    enabled: !!tenantId,  // SECURITY: Don't fetch without tenant context
  });
};

/**
 * Fetch cases using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 * Caching: 1 minute staleTime
 * @param tenantId - Required tenant UUID for security filtering
 * @param storeId - Optional store UUID to filter by
 */
export const useCases = (tenantId?: string, storeId?: string) => {
  return useQuery({
    queryKey: queryKeys.cases(tenantId!, storeId),
    queryFn: () => api.getCases(tenantId!, storeId),
    staleTime: STALE_TIMES.CASES,
    enabled: !!tenantId,  // SECURITY: Don't fetch without tenant context
  });
};

/**
 * Fetch tasks using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 * Caching: 1 minute staleTime
 * @param tenantId - Required tenant UUID for security filtering
 * @param storeId - Optional store UUID to filter by
 */
export const useTasks = (tenantId?: string, storeId?: string) => {
  return useQuery({
    queryKey: queryKeys.tasks(tenantId!, storeId),
    queryFn: () => api.getTasks(tenantId!, storeId),
    staleTime: STALE_TIMES.TASKS,
    enabled: !!tenantId,  // SECURITY: Don't fetch without tenant context
  });
};

/**
 * Fetch store zones using TanStack Query.
 */
export const useStoreZones = (storeId: string) => {
  return useQuery({
    queryKey: queryKeys.storeZones(storeId),
    queryFn: () => api.getStoreZones(storeId),
    staleTime: STALE_TIMES.DEFAULT,
    enabled: !!storeId,
  });
};

// ============================================
// STORE STATUS HOOKS (Real Supabase Data)
// ============================================

/**
 * Fetch stores filtered by tenant ID for proper tenant isolation using TanStack Query.
 * SECURITY: Always filters by tenantId to prevent cross-tenant data leaks.
 * Uses pre-computed view_dashboard_kpis for performance.
 * Caching: 2 minute staleTime
 *
 * @see supabase/migrations/20260122200000_cqrs_lite_optimization.sql
 */
export const useStoreStatuses = (tenantId?: string) => {
  const fetchStoreStatuses = async (): Promise<StoreStatus[]> => {
    // SECURITY: Require tenantId to prevent loading all stores
    if (!tenantId) {
      if (import.meta.env.DEV) console.warn('[useStoreStatuses] No tenantId provided - returning empty to prevent data leak');
      return [];
    }

    try {
      // CQRS-Lite: Fetch pre-computed KPIs from materialized view
      // This includes open_alerts, shrinkage, and sales pre-aggregated
      const { data: viewData, error: viewError } = await supabase
        .from('view_dashboard_kpis')
        .select('store_id, store_name, total_sales_30d, shrinkage_30d, shrinkage_rate, open_alerts, critical_alerts')
        .eq('tenant_id', tenantId);

      // Fetch store coordinates separately (not in materialized view)
      const { data: stores, error: storesError } = await supabase
        .from('stores')
        .select('id, name, location, coordinates')
        .eq('tenant_id', tenantId)
        .order('name');

      if (storesError) {
        if (isSchemasCacheError(storesError)) {
          if (import.meta.env.DEV) console.debug('Stores table not available');
        }
        return [];
      }

      if (!stores || stores.length === 0) {
        return [];
      }

      // Build lookup map from materialized view
      const kpiMap: Record<string, {
        totalSales: number;
        shrinkage: number;
        shrinkageRate: number;
        openAlerts: number;
        criticalAlerts: number;
      }> = {};

      if (viewData && !viewError) {
        for (const row of viewData) {
          kpiMap[row.store_id] = {
            totalSales: Number(row.total_sales_30d) || 0,
            shrinkage: Number(row.shrinkage_30d) || 0,
            shrinkageRate: Number(row.shrinkage_rate) || 0,
            openAlerts: Number(row.open_alerts) || 0,
            criticalAlerts: Number(row.critical_alerts) || 0,
          };
        }
        if (import.meta.env.DEV) console.debug(`[useStoreStatuses] Using pre-computed view_dashboard_kpis for ${Object.keys(kpiMap).length} stores`);
      } else {
        // Fallback: fetch from separate tables if view not available
        if (import.meta.env.DEV) console.debug('[useStoreStatuses] view_dashboard_kpis not available, using fallback');
        const storeIds = stores.map(s => s.id);

        // Fetch open alerts count per store — SECURITY: defense-in-depth with tenant_id
        const { data: alertCounts } = await supabase
          .from('alerts')
          .select('store_id')
          .eq('tenant_id', tenantId)
          .in('store_id', storeIds)
          .in('status', ['new', 'investigating']);

        // Fetch shrinkage KPIs per store — SECURITY: defense-in-depth with tenant_id
        let shrinkageQuery = supabase
          .from('dashboard_kpis')
          .select('store_id, value')
          .in('store_id', storeIds)
          .eq('kpi_type', 'shrinkage')
          .eq('period', '30d');
        shrinkageQuery = shrinkageQuery.eq('tenant_id', tenantId!);
        const { data: kpis } = await shrinkageQuery;

        // Fetch from daily_store_stats for sales (more efficient than raw transactions)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // SECURITY: defense-in-depth with tenant_id
        let dailyStatsQuery = supabase
          .from('daily_store_stats')
          .select('store_id, total_revenue')
          .in('store_id', storeIds)
          .gte('stat_date', thirtyDaysAgo.toISOString().split('T')[0]);
        dailyStatsQuery = dailyStatsQuery.eq('tenant_id', tenantId!);
        const { data: dailyStats } = await dailyStatsQuery;

        // Build lookup maps
        const alertCountMap: Record<string, number> = {};
        for (const alert of (alertCounts || [])) {
          alertCountMap[alert.store_id] = (alertCountMap[alert.store_id] || 0) + 1;
        }

        const shrinkageMap: Record<string, number> = {};
        for (const kpi of (kpis || [])) {
          shrinkageMap[kpi.store_id] = Number(kpi.value) || 0;
        }

        const salesMap: Record<string, number> = {};
        for (const stat of (dailyStats || [])) {
          salesMap[stat.store_id] = (salesMap[stat.store_id] || 0) + (Number(stat.total_revenue) || 0);
        }

        // Populate kpiMap from fallback data
        for (const store of stores) {
          const totalSales = salesMap[store.id] || 0;
          const shrinkage = shrinkageMap[store.id] || 0;
          kpiMap[store.id] = {
            totalSales,
            shrinkage,
            shrinkageRate: totalSales > 0 ? (shrinkage / totalSales) * 100 : 0,
            openAlerts: alertCountMap[store.id] || 0,
            criticalAlerts: 0, // Not available in fallback
          };
        }
      }

      // Map stores to StoreStatus format with pre-computed data
      return stores.map(store => {
        // Parse coordinates if it's a JSON object
        let coords = { x: 50, y: 50 };
        if (store.coordinates && typeof store.coordinates === 'object') {
          const c = store.coordinates as { x?: number; y?: number };
          coords = { x: c.x || 50, y: c.y || 50 };
        }

        const kpis = kpiMap[store.id] || { totalSales: 0, shrinkage: 0, shrinkageRate: 0, openAlerts: 0, criticalAlerts: 0 };

        // Calculate shrinkage rate as percentage of sales
        const shrinkageRate = fmtPct(kpis.shrinkageRate);

        // Calculate risk level (0-100) based on alerts and shrinkage
        const alertRisk = Math.min(kpis.openAlerts * 10, 50); // Up to 50 points from alerts
        const shrinkageRisk = Math.min(kpis.shrinkageRate * 20, 50); // Up to 50 points from shrinkage
        const riskLevel = Math.round(alertRisk + shrinkageRisk);

        return {
          store: {
            id: store.id,
            name: store.name,
            location: store.location || 'Unknown',
            coordinates: coords,
          },
          riskLevel: Math.min(riskLevel, 100),
          openAlerts: kpis.openAlerts,
          shrinkageRate,
        };
      });
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to fetch store statuses:', err);
      return [];
    }
  };

  return useQuery({
    queryKey: queryKeys.storeStatuses(tenantId || ''),
    queryFn: fetchStoreStatuses,
    staleTime: STALE_TIMES.STORE_STATUSES,
    enabled: !!tenantId,
  });
};

// ============================================
// ANALYTICS HOOKS (Placeholder until analytics tables exist)
// ============================================

/**
 * Fetch KPIs for a store using TanStack Query.
 * Uses pre-computed view_dashboard_kpis materialized view for performance.
 * Caching: 5 minute staleTime (aggregated metrics, less volatile)
 *
 * @see supabase/migrations/20260122200000_cqrs_lite_optimization.sql
 */
export const useKPIs = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.kpis(tenantId!, storeId),
    queryFn: async (): Promise<KPI[]> => {
      try {
        // CQRS-Lite: Fetch from pre-computed materialized view
        // This view aggregates data from daily_store_stats, inventory, and alerts
        // SECURITY: tenantId guaranteed by enabled guard
        const { data: viewData, error: viewError } = await supabase
          .from('view_dashboard_kpis')
          .select('*')
          .eq('tenant_id', tenantId!)
          .eq('store_id', storeId)
          .single();

        if (viewError && !isSchemasCacheError(viewError)) {
          if (import.meta.env.DEV) console.debug('[useKPIs] view_dashboard_kpis not available, falling back to dashboard_kpis');
        }

        // If materialized view is available, use pre-computed values
        if (viewData) {
          const kpis: KPI[] = [];

          // CANONICAL IRA: Prefer inventory_quality_metrics over view_dashboard_kpis
          // See services/metricCalculations.ts METRIC_SOURCES.ira for priority chain
          let canonicalIRA: number = 0;
          const { data: qualityRow } = await supabase
            .from('inventory_quality_metrics')
            .select('ira')
            .eq('tenant_id', tenantId!)
            .eq('store_id', storeId)
            .order('calculated_at', { ascending: false })
            .limit(1)
            .single();

          if (qualityRow?.ira !== null && qualityRow?.ira !== undefined) {
            canonicalIRA = Number(qualityRow.ira);
          } else {
            // Fallback: use view_dashboard_kpis.ira (NEVER default to 100)
            canonicalIRA = Number(viewData.ira || 0);
          }

          // Shrinkage KPI
          kpis.push({
            id: 'shrinkage',
            title: 'Merma',
            value: fmtCompact(Number(viewData.shrinkage_30d || 0)),
            trend: 0, // Trend requires historical comparison
            description: 'Pérdida de inventario por robo, daño o error',
            history: [],
            enabled: true
          });

          // Inventory Accuracy KPI (IRA) — uses canonical source
          kpis.push({
            id: 'inventory_accuracy',
            title: 'Precisión de Inventario',
            value: fmtPct(canonicalIRA),
            trend: 0,
            description: 'Coincidencia entre stock físico y sistema',
            history: [],
            enabled: true
          });

          // Open Alerts KPI
          kpis.push({
            id: 'open_alerts',
            title: 'Alertas Abiertas',
            value: String(viewData.open_alerts || 0),
            trend: 0,
            description: 'Alertas pendientes de resolución',
            history: [],
            enabled: true
          });

          // False Positives - fetch from dashboard_kpis (not in materialized view)
          const { data: fpData } = await supabase
            .from('dashboard_kpis')
            .select('value, trend')
            .eq('tenant_id', tenantId!)
            .eq('store_id', storeId)
            .eq('kpi_type', 'false_positives')
            .single();

          kpis.push({
            id: 'false_positives',
            title: 'Falsos Positivos',
            value: fmtPct(Number(fpData?.value || 0)),
            trend: Number(fpData?.trend || 0),
            description: 'Alertas descartadas como falsa alarma',
            history: [],
            enabled: true
          });

          // Price Override Rate - fetch from dashboard_kpis
          const { data: overrideRateData } = await supabase
            .from('dashboard_kpis')
            .select('value, metadata')
            .eq('tenant_id', tenantId!)
            .eq('store_id', storeId)
            .eq('kpi_type', 'price_override_rate')
            .single();

          kpis.push({
            id: 'price_override_rate',
            title: 'Tasa Cambio de Precio',
            value: fmtPct(Number(overrideRateData?.value || 0)),
            trend: 0,
            description: 'Transacciones con cambio manual de precio en POS',
            history: [],
            enabled: true
          });

          // Override Value Loss - fetch from dashboard_kpis
          const { data: overrideLossData } = await supabase
            .from('dashboard_kpis')
            .select('value, metadata')
            .eq('tenant_id', tenantId!)
            .eq('store_id', storeId)
            .eq('kpi_type', 'override_value_loss')
            .single();

          kpis.push({
            id: 'override_value_loss',
            title: 'Pérdida por Override',
            value: fmtCompact(Number(overrideLossData?.value || 0)),
            trend: 0,
            description: 'Valor perdido por reducciones de precio',
            history: [],
            enabled: true
          });

          return kpis;
        }

        // Fallback: Fetch from dashboard_kpis table if view not available
        // SECURITY: tenantId guaranteed by enabled guard
        const { data, error } = await supabase
          .from('dashboard_kpis')
          .select('*')
          .eq('tenant_id', tenantId!)
          .eq('store_id', storeId);

        if (error) {
          if (isSchemasCacheError(error)) {
            if (import.meta.env.DEV) console.debug('dashboard_kpis table not available');
          } else {
            if (import.meta.env.DEV) console.error('Failed to fetch dashboard KPIs:', error);
          }
          return [];
        }

        if (!data || data.length === 0) {
          return [];
        }

        // Transform database records to KPI format
        const kpiMap: Record<string, KPI> = {};

        // Define KPI metadata for transformation
        const kpiMetadata: Record<string, { title: string; description: string; formatValue: (v: number) => string }> = {
          shrinkage: {
            title: 'Merma',
            description: 'Pérdida de inventario por robo, daño o error',
            formatValue: (v: number) => fmtCompact(v)
          },
          inventory_accuracy: {
            title: 'Precisión de Inventario',
            description: 'Coincidencia entre stock físico y sistema',
            formatValue: (v: number) => fmtPct(v)
          },
          open_alerts: {
            title: 'Alertas Abiertas',
            description: 'Alertas pendientes de resolución',
            formatValue: (v: number) => v.toString()
          },
          false_positives: {
            title: 'Falsos Positivos',
            description: 'Alertas descartadas como falsa alarma',
            formatValue: (v: number) => fmtPct(v)
          },
          price_override_rate: {
            title: 'Tasa Cambio de Precio',
            description: 'Transacciones con cambio manual de precio en POS',
            formatValue: (v: number) => fmtPct(v)
          },
          override_value_loss: {
            title: 'Pérdida por Override',
            description: 'Valor perdido por reducciones de precio',
            formatValue: (v: number) => fmtCompact(v)
          },
          // POS Suspicious Activity KPIs
          void_rate: {
            title: 'Tasa de Anulaciones',
            description: 'Transacciones anuladas como % del total',
            formatValue: (v: number) => fmtPct(v)
          },
          refund_rate: {
            title: 'Tasa de Devoluciones',
            description: 'Devoluciones como % de ventas',
            formatValue: (v: number) => fmtPct(v)
          },
          no_sale_rate: {
            title: 'Tasa No-Sale',
            description: 'Aperturas de caja sin transacción (por 100 txns)',
            formatValue: (v: number) => fmtPct(v)
          },
          post_void_rate: {
            title: 'Tasa Post-Anulación',
            description: 'Anulaciones dentro de 5 min de venta original (CRÍTICO)',
            formatValue: (v: number) => fmtPct(v)
          },
          discount_rate: {
            title: 'Tasa Descuentos',
            description: 'Ventas con descuento manual',
            formatValue: (v: number) => fmtPct(v)
          },
          avg_discount_amount: {
            title: 'Descuento Promedio',
            description: 'Monto promedio de descuento cuando se aplica',
            formatValue: (v: number) => fmtScore(v, 2)
          },
          suspicious_activity_score: {
            title: 'Score Actividad Sospechosa',
            description: 'Puntuación compuesta de riesgo POS (0-100)',
            formatValue: (v: number) => fmtScore(v, 0)
          }
        };

        const allowedKpiTypes = [
          'shrinkage', 'inventory_accuracy', 'open_alerts', 'false_positives',
          'price_override_rate', 'override_value_loss',
          'void_rate', 'refund_rate', 'no_sale_rate', 'post_void_rate',
          'discount_rate', 'avg_discount_amount', 'suspicious_activity_score'
        ] as const;

        for (const row of data) {
          const kpiType = row.kpi_type as string;
          if ((allowedKpiTypes as readonly string[]).includes(kpiType) && kpiMetadata[kpiType]) {
            const meta = kpiMetadata[kpiType];
            kpiMap[kpiType] = {
              id: kpiType as KPI['id'],
              title: meta.title,
              value: meta.formatValue(Number(row.value) || 0),
              trend: Number(row.trend) || 0,
              description: meta.description,
              history: [],
              enabled: true
            };
          }
        }

        return allowedKpiTypes
          .filter(type => kpiMap[type])
          .map(type => kpiMap[type]);

      } catch (err) {
        if (import.meta.env.DEV) console.error('Error fetching KPIs:', err);
        return [];
      }
    },
    staleTime: STALE_TIMES.KPI, // 5 minutes
    enabled: !!tenantId && !!storeId,
    initialData: [],
    // initialData counts as fresh for staleTime unless dated in the past — without
    // this the KPI strip never fetched on mount and stayed empty.
    initialDataUpdatedAt: 0,
  });
};

/**
 * Fetch risk patterns using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 * @param tenantId - Required tenant UUID for security filtering
 * @param storeId - The store UUID
 */
export const useRiskPatterns = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.riskPatterns(storeId),
    queryFn: () => AlertRepository.getRiskPatterns(tenantId!, storeId),
    staleTime: STALE_TIMES.DEFAULT,
    enabled: !!tenantId && !!storeId,  // SECURITY: Don't fetch without tenant context
  });
};

/** @deprecated Stub — returns empty data. Will be replaced with real analytics. */
export const useChartData = (_storeId: string) => {
  return useQuery({
    queryKey: ['chartData', _storeId] as const,
    queryFn: async (): Promise<{ rootCause: RootCauseData[], trend: TrendData[] }> => ({ rootCause: [], trend: [] }),
    staleTime: STALE_TIMES.DEFAULT,
  });
};

/** @deprecated Stub — returns empty data. Will be replaced with real reports table. */
export const useReports = () => {
  return useQuery({
    queryKey: ['reports'] as const,
    queryFn: async (): Promise<Report[]> => [],
    staleTime: STALE_TIMES.DEFAULT,
  });
};

/**
 * Fetch KPI detail drill-down data for modals using TanStack Query.
 * Returns pre-computed data from materialized views where available.
 * Caching: 5 minute staleTime (analytical data)
 */
export const useKpiDetails = (tenantId: string | undefined, kpiId: KPI['id'], storeId: string) => {
  return useQuery({
    queryKey: queryKeys.kpiDetails(kpiId, storeId),
    staleTime: STALE_TIMES.KPI_DETAILS, // 5 minutes - analytical data
    queryFn: async () => {
      switch (kpiId) {
        case 'shrinkage': {
          // Get shrinkage data by product
          const { data: inventory } = await supabase
            .from('inventory')
            .select(`
              system_qty,
              physical_qty,
              products (
                name,
                unit_price,
                category
              )
            `)
            .eq('tenant_id', tenantId!)
            .eq('store_id', storeId);

          if (!inventory || inventory.length === 0) {
            return null;
          }

          // Calculate losses by product and category
          const productLosses: { product: string; loss: number; category: string }[] = [];
          const categoryLosses: Record<string, number> = {};

          for (const inv of inventory) {
            const product = inv.products;
            if (!product) continue;

            const variance = (inv.system_qty || 0) - (inv.physical_qty || 0);
            if (variance > 0) {
              const loss = variance * (product.unit_price || 0);
              productLosses.push({
                product: product.name,
                loss,
                category: product.category || 'Other'
              });
              categoryLosses[product.category || 'Other'] = (categoryLosses[product.category || 'Other'] || 0) + loss;
            }
          }

          // Sort by loss descending
          productLosses.sort((a, b) => b.loss - a.loss);

          // Build root causes (approximated from category distribution)
          const totalLoss = productLosses.reduce((sum, p) => sum + p.loss, 0);
          const rootCauses = Object.entries(categoryLosses)
            .map(([name, value]) => ({
              name,
              value: totalLoss > 0 ? Math.round((value / totalLoss) * 100) : 0
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 4);

          return {
            rootCauses,
            topProducts: productLosses.slice(0, 10).map(p => ({
              product: p.product,
              loss: p.loss
            }))
          };
        }

        case 'inventory_accuracy': {
          // Get accuracy breakdown by category
          const { data: inventory } = await supabase
            .from('inventory')
            .select(`
              system_qty,
              physical_qty,
              products (
                name,
                sku,
                category
              )
            `)
            .eq('tenant_id', tenantId!)
            .eq('store_id', storeId);

          if (!inventory || inventory.length === 0) {
            return null;
          }

          // Calculate discrepancies
          const discrepancies: { sku: string; name: string; system: number; physical: number; variance: number }[] = [];
          const categoryAccuracy: Record<string, { total: number; accurate: number }> = {};

          for (const inv of inventory) {
            const product = inv.products;
            if (!product) continue;

            const systemQty = inv.system_qty || 0;
            const physicalQty = inv.physical_qty || 0;
            const variance = systemQty - physicalQty;
            const category = product.category || 'Other';

            if (!categoryAccuracy[category]) {
              categoryAccuracy[category] = { total: 0, accurate: 0 };
            }
            categoryAccuracy[category].total++;
            if (Math.abs(variance) <= 1) { // Allow variance of 1 as accurate
              categoryAccuracy[category].accurate++;
            }

            if (Math.abs(variance) > 0) {
              discrepancies.push({
                sku: product.sku,
                name: product.name,
                system: systemQty,
                physical: physicalQty,
                variance
              });
            }
          }

          // Sort by absolute variance
          discrepancies.sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));

          // Build category accuracy breakdown
          const categoryBreakdown = Object.entries(categoryAccuracy).map(([category, data]) => ({
            category,
            accuracy: data.total > 0 ? Math.round((data.accurate / data.total) * 100) : 100
          }));

          return {
            categoryBreakdown,
            topDiscrepancies: discrepancies.slice(0, 10)
          };
        }

        case 'open_alerts': {
          // Get alerts breakdown by type and category
          const { data: alerts } = await supabase
            .from('alerts')
            .select('type, category, status, created_at')
            .eq('tenant_id', tenantId!)
            .eq('store_id', storeId)
            .in('status', ['new', 'investigating']);

          if (!alerts || alerts.length === 0) {
            return null;
          }

          // Group by type
          const byType: Record<string, number> = {};
          const byCategory: Record<string, number> = {};

          for (const alert of alerts) {
            byType[alert.type || 'unknown'] = (byType[alert.type || 'unknown'] || 0) + 1;
            byCategory[alert.category || 'general'] = (byCategory[alert.category || 'general'] || 0) + 1;
          }

          return {
            byType: Object.entries(byType).map(([type, count]) => ({ type, count })),
            byCategory: Object.entries(byCategory).map(([category, count]) => ({ category, count })),
            totalAlerts: alerts.length
          };
        }

        case 'false_positives': {
          // Get false positive rate by alert type
          // Alerts query uses typed columns from regenerated database.types.ts
          const { data: resolvedAlerts } = await supabase
            .from('alerts')
            .select('type, status, metadata')
            .eq('tenant_id', tenantId!)
            .eq('store_id', storeId)
            .in('status', ['resolved', 'dismissed']);

          if (!resolvedAlerts || resolvedAlerts.length === 0) {
            return null;
          }

          // Calculate FP rate by type
          // Resolution is stored in metadata or inferred from status
          const typeStats: Record<string, { total: number; fp: number }> = {};

          for (const alert of resolvedAlerts) {
            const type = alert.type || 'unknown';
            if (!typeStats[type]) {
              typeStats[type] = { total: 0, fp: 0 };
            }
            typeStats[type].total++;
            // Check for false positive in metadata or status
            const resolution = alert.metadata?.resolution || '';
            if (alert.status === 'dismissed' || resolution === 'false_positive' || resolution === 'dismissed_false_alarm') {
              typeStats[type].fp++;
            }
          }

          const byType = Object.entries(typeStats).map(([type, stats]) => ({
            type,
            total: stats.total,
            falsePositives: stats.fp,
            rate: stats.total > 0 ? Math.round((stats.fp / stats.total) * 100) : 0
          })).sort((a, b) => b.rate - a.rate);

          const totalFP = resolvedAlerts.filter((a: AlertResolutionRow) => {
            const resolution = a.metadata?.resolution || '';
            return a.status === 'dismissed' || resolution === 'false_positive' || resolution === 'dismissed_false_alarm';
          }).length;

          return {
            byType,
            totalResolved: resolvedAlerts.length,
            totalFP
          };
        }

        default:
          return null;
      }
    },
    enabled: !!tenantId && !!kpiId && !!storeId,
  });
};

/**
 * Fetch operational compliance metrics using TanStack Query.
 * Caching: 1 minute staleTime
 */
export const useOperationalCompliance = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.operationalCompliance(tenantId!, storeId),
    queryFn: async (): Promise<OperationalCompliance> => {
      // Fetch compliance score from backend (dashboard_kpis)
      // The score is pre-calculated by PostgreSQL trigger on task changes
      // SECURITY: defense-in-depth with tenant_id
      let complianceKpiQuery = supabase
        .from('dashboard_kpis')
        .select('value, metadata')
        .eq('store_id', storeId)
        .eq('kpi_type', 'compliance_score')
        .eq('period', '30d');
      complianceKpiQuery = complianceKpiQuery.eq('tenant_id', tenantId!);
      const [kpiResult, tasksResult] = await Promise.all([
        complianceKpiQuery.single(),
        // SECURITY: Always filter by tenant_id to prevent cross-tenant leakage
        supabase
          .from('tasks')
          .select('id, title, status, priority, due_date, completed_at')
          .eq('tenant_id', tenantId!)
          .eq('store_id', storeId)
      ]);

      // Use backend score if available, otherwise default to 0
      const backendScore = kpiResult.data?.value ? Math.round(Number(kpiResult.data.value)) : 0;

      const tasks = tasksResult.data;
      if (tasksResult.error || !tasks || tasks.length === 0) {
        return { score: backendScore, keyAreas: [] };
      }

      // Group tasks by priority as areas (keyAreas still computed locally for breakdown)
      const areaMap: Record<string, { total: number; completed: number; tasks: typeof tasks }> = {};

      for (const task of tasks) {
        const area = task.priority || 'medium';
        if (!areaMap[area]) {
          areaMap[area] = { total: 0, completed: 0, tasks: [] };
        }
        areaMap[area].total++;
        if (task.status === 'completed') {
          areaMap[area].completed++;
        }
        areaMap[area].tasks.push(task);
      }

      // Create key areas from task priorities
      const areaLabels: Record<string, string> = {
        urgent: 'Tareas Urgentes',
        high: 'Prioridad Alta',
        medium: 'Prioridad Media',
        low: 'Prioridad Baja'
      };

      const keyAreas = Object.entries(areaMap).map(([area, data]) => {
        const score = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
        const pendingTasks = data.tasks.filter((t) => t.status !== 'completed');
        const lastCompletedTask = data.tasks
          .filter((t) => t.completed_at)
          .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())[0];

        return {
          name: areaLabels[area] || area,
          status: (score >= 80 ? 'ok' : score >= 50 ? 'warning' : 'critical') as 'ok' | 'warning' | 'critical',
          details: pendingTasks.length > 0
            ? `${pendingTasks.length} tarea(s) pendiente(s)`
            : 'Todas las tareas completadas',
          lastCheck: lastCompletedTask?.completed_at || new Date().toISOString()
        };
      });

      return {
        score: backendScore,
        keyAreas
      };
    },
    staleTime: STALE_TIMES.OPERATIONAL_COMPLIANCE,
    enabled: !!tenantId && !!storeId,
    // Provide default empty state (dated in the past so it is refetched on mount)
    initialData: { score: 0, keyAreas: [] },
    initialDataUpdatedAt: 0,
  });
};

/**
 * Fetch inventory insights using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 * Caching: 1 minute staleTime
 * @param tenantId - Required tenant UUID for security filtering
 * @param storeId - The store UUID
 */
export const useInventoryInsights = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.inventoryInsights(storeId),
    queryFn: () => InventoryRepository.getInventoryInsights(tenantId!, storeId),
    staleTime: STALE_TIMES.INVENTORY_INSIGHTS,
    enabled: !!tenantId && !!storeId,  // SECURITY: Don't fetch without tenant context
  });
};


/**
 * Fetch risk trend data using TanStack Query.
 * Caching: 5 minute staleTime (historical/analytical data)
 */
export const useRiskTrend = (tenantId: string | undefined, storeId: string, days: number) => {
  return useQuery({
    queryKey: queryKeys.riskTrend(storeId, days),
    staleTime: STALE_TIMES.RISK_TREND, // 5 minutes - historical trend data
    queryFn: async (): Promise<RiskTrendPoint[]> => {
      // Generate risk trend from alerts and zone_metrics historical data
      let zoneQuery = supabase
        .from('zone_metrics')
        .select('risk_score, measured_at')
        .eq('store_id', storeId)
        .order('measured_at', { ascending: true })
        .limit(days);
      zoneQuery = zoneQuery.eq('tenant_id', tenantId!);
      const { data: zoneMetrics, error: zoneError } = await zoneQuery;

      if (zoneError) {
        if (import.meta.env.DEV) console.error(`[useRiskTrend] Error fetching zone_metrics (HTTP: ${zoneError.code}):`, zoneError.message);
        return [];
      }

      if (!zoneMetrics || zoneMetrics.length === 0) {
        if (import.meta.env.DEV) console.debug(`[useRiskTrend] No zone metrics found for store ${storeId}`);
        return [];
      }

      // Transform zone metrics into trend points - matching RiskTrendPoint interface
      const trendPoints: RiskTrendPoint[] = zoneMetrics.map((metric, index) => {
        const date = new Date(metric.measured_at);
        const isLastThree = index >= zoneMetrics.length - 3;
        const currentValue = Math.round(Number(metric.risk_score) * 100);
        const prevIndex = index > 0 ? index - 1 : 0;
        const prevValue = Math.round(Number(zoneMetrics[prevIndex].risk_score) * 100);

        return {
          date: date.toLocaleDateString('es-CL', { month: 'short', day: 'numeric' }),
          value: isLastThree ? null : currentValue,
          previousValue: isLastThree ? null : prevValue,
          forecast: isLastThree ? currentValue : undefined
        };
      });

      return trendPoints;
    },
    enabled: !!tenantId && !!storeId,
  });
};

/**
 * Fetch global overview stats using TanStack Query.
 * SECURITY: Requires tenantId to prevent cross-tenant data leakage.
 * Caching: 5 minute staleTime (chain-wide aggregates)
 */
export const useGlobalOverview = (tenantId?: string) => {
  return useQuery({
    queryKey: queryKeys.globalOverview(tenantId),
    queryFn: () => AlertRepository.getGlobalOverview(tenantId),
    staleTime: STALE_TIMES.GLOBAL_OVERVIEW, // 5 minutes - chain-wide aggregates
    enabled: !!tenantId, // Only fetch when tenantId is available
  });
};

/**
 * Fetch inventory quality metrics for a store using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 * Uses pre-computed inventory_quality_metrics table.
 * Caching: 5 minute staleTime (analytical data from pre-computed views)
 * @param tenantId - Required tenant UUID for security filtering
 * @param storeId - The store UUID
 */
export const useInventoryQuality = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.inventoryQuality(storeId),
    queryFn: () => InventoryRepository.getInventoryQuality(tenantId!, storeId),
    staleTime: STALE_TIMES.INVENTORY_QUALITY, // 5 minutes - pre-computed metrics
    enabled: !!tenantId && !!storeId,  // SECURITY: Don't fetch without tenant context
  });
};

// ============================================
// FINANCIAL INTELLIGENCE HOOKS (Placeholder)
// ============================================

/**
 * Fetch store financial summary using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 * Uses pre-computed data from daily_store_stats table.
 * Caching: 5 minute staleTime (analytical data)
 * @param tenantId - Required tenant UUID for security filtering
 * @param storeId - The store UUID
 */
export const useStoreFinancials = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.storeFinancials(storeId),
    queryFn: () => InventoryRepository.getStoreFinancials(tenantId!, storeId),
    staleTime: STALE_TIMES.STORE_FINANCIALS, // 5 minutes - pre-computed financials
    enabled: !!tenantId && !!storeId,  // SECURITY: Don't fetch without tenant context
  });
};

/**
 * Extended financial data for the Financial Dashboard.
 * Calculates GMROI, shrinkage rate, inventory turnover, and trends from real data.
 */
export interface ExtendedFinancialData {
  gmroi: number;
  gmroiTrend: number;
  shrinkageRate: number;
  shrinkageTrend: number;
  grossMargin: number;
  avgInventoryCost: number;
  inventoryTurnover: number;
  deadStockValue: number;
  gmroiByCategory: Array<{ category: string; gmroi: number; grossMargin: number }>;
  gmroiTrendHistory: Array<{ date: string; gmroi: number }>;
  shrinkageTrendHistory: Array<{ date: string; value: number }>;
}

/**
 * Fetch extended financial data using TanStack Query.
 * Uses pre-computed view_dashboard_kpis and daily_store_stats for performance.
 * Caching: 2 minute staleTime (heavy calculation, less volatile)
 *
 * @see supabase/migrations/20260122200000_cqrs_lite_optimization.sql
 */
export const useExtendedFinancialData = (storeId: string, tenantId?: string) => {
  return useQuery({
    queryKey: queryKeys.extendedFinancials(tenantId!, storeId),
    queryFn: async (): Promise<ExtendedFinancialData | null> => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const startDate = thirtyDaysAgo.toISOString().split('T')[0];
      const endDate = new Date().toISOString().split('T')[0];

      if (import.meta.env.DEV) console.debug(`[useExtendedFinancialData] Fetching for storeId: ${storeId}, dateRange: ${startDate} to ${endDate}`);

      // Fetch backend-calculated KPIs from inventory_quality_metrics
      // Actual columns: gmroi, doh, sell_through, ira, phantom_rate, shrinkage_rate, total_shrinkage_value
      let qualityQuery = supabase
        .from('inventory_quality_metrics')
        .select('gmroi, doh, sell_through, shrinkage_rate, total_shrinkage_value')
        .eq('store_id', storeId)
        .eq('period', '30d')
        .order('calculated_at', { ascending: false })
        .limit(1);
      qualityQuery = qualityQuery.eq('tenant_id', tenantId!);
      const { data: qualityMetrics } = await qualityQuery.single();

      const backendFillRate: number | null = null; // fill_rate column not yet in inventory_quality_metrics
      const backendInventoryTurnover: number | null = null; // inventory_turnover column not yet in inventory_quality_metrics
      const backendDeadStockValue = qualityMetrics?.total_shrinkage_value ? Number(qualityMetrics.total_shrinkage_value) : null;

      // CQRS-Lite: Fetch pre-computed KPIs from materialized view
      // SECURITY: defense-in-depth with tenant_id
      let viewQuery = supabase
        .from('view_dashboard_kpis')
        .select('*')
        .eq('store_id', storeId);
      viewQuery = viewQuery.eq('tenant_id', tenantId!);
      const { data: viewData, error: viewError } = await viewQuery.single();

      if (viewError && !isSchemasCacheError(viewError)) {
        if (import.meta.env.DEV) console.debug('[useExtendedFinancialData] view_dashboard_kpis not available, falling back to raw queries');
      }

      // CQRS-Lite: Fetch aggregated daily stats for trends
      let dailyStatsQuery = supabase
        .from('daily_store_stats')
        .select('stat_date, total_revenue, gross_margin, shrinkage_value')
        .eq('store_id', storeId)
        .gte('stat_date', startDate)
        .order('stat_date', { ascending: true });
      dailyStatsQuery = dailyStatsQuery.eq('tenant_id', tenantId!);
      const { data: dailyStats, error: dailyStatsError } = await dailyStatsQuery;

      if (dailyStatsError && !isSchemasCacheError(dailyStatsError)) {
        if (import.meta.env.DEV) console.debug('[useExtendedFinancialData] daily_store_stats not available');
      }
      if (import.meta.env.DEV) console.debug(`[useExtendedFinancialData] Daily stats fetched: ${dailyStats?.length ?? 0}`);

      // If materialized view is available, use pre-computed values
      if (viewData) {
        if (import.meta.env.DEV) console.debug('[useExtendedFinancialData] Using pre-computed view_dashboard_kpis');

        // Fetch inventory for category breakdown (still needed for detailed analysis)
        // SECURITY: defense-in-depth with tenant_id
        let invQuery = supabase
          .from('inventory')
          .select('product_id, system_qty, physical_qty, products!inner(unit_cost, unit_price, category)')
          .eq('store_id', storeId);
        invQuery = invQuery.eq('tenant_id', tenantId!);
        const { data: inventory } = await invQuery;

        // Build category metrics from inventory
        const categoryMetrics: Record<string, { inventoryCost: number; grossMargin: number }> = {};
        let deadStockValue = 0;

        // Track products with sales from daily_store_stats
        const totalRevenue = Number(viewData.total_sales_30d) || 0;
        const totalCOGS = Number(viewData.total_cogs_30d) || 0;
        const grossMargin = Number(viewData.gross_margin_30d) || 0;
        const inventoryCost = Number(viewData.inventory_cost) || 0;
        const shrinkageValue = Number(viewData.shrinkage_30d) || 0;

        for (const inv of (inventory || [])) {
          const unitCost = inv.products?.unit_cost || 0;
          const category = inv.products?.category || 'Other';
          const qty = inv.physical_qty || inv.system_qty || 0;
          const itemInventoryValue = qty * unitCost;

          if (!categoryMetrics[category]) {
            categoryMetrics[category] = { inventoryCost: 0, grossMargin: 0 };
          }
          categoryMetrics[category].inventoryCost += itemInventoryValue;

          // No Mock Data Policy: Dead stock calculation requires specific business rules
          // tracked in inventory_quality_metrics. Skip synthetic estimation here.
          // Dead stock value will come from backendDeadStockValue if available.
        }

        // Distribute gross margin proportionally across categories
        const totalCategoryCost = Object.values(categoryMetrics).reduce((sum, c) => sum + c.inventoryCost, 0);
        for (const category of Object.keys(categoryMetrics)) {
          const proportion = totalCategoryCost > 0 ? categoryMetrics[category].inventoryCost / totalCategoryCost : 0;
          categoryMetrics[category].grossMargin = grossMargin * proportion;
        }

        // Build GMROI by category
        const gmroiByCategory = Object.entries(categoryMetrics)
          .filter(([_, metrics]) => metrics.inventoryCost > 0)
          .map(([category, metrics]) => ({
            category,
            gmroi: metrics.inventoryCost > 0
              ? Math.round((metrics.grossMargin / metrics.inventoryCost) * 100) / 100
              : 0,
            grossMargin: metrics.grossMargin
          }))
          .sort((a, b) => b.gmroi - a.gmroi)
          .slice(0, 6);

        // Build trend histories from daily_store_stats
        // NOTE: Trends are empty until daily_store_stats is populated.
        // The table is populated by: (1) trigger on new transaction INSERTs,
        // (2) calling api.backfillDailyStoreStats() for historical data.
        // Use Settings > Backfill Stats button after importing CSV data.
        const gmroiTrendHistory: Array<{ date: string; gmroi: number }> = [];
        const shrinkageTrendHistory: Array<{ date: string; value: number }> = [];

        if (dailyStats && dailyStats.length > 0) {
          for (const day of dailyStats.slice(-7)) {
            const dayRevenue = Number(day.total_revenue) || 0;
            const dayShrinkage = Number(day.shrinkage_value) || 0;
            const shrinkageRate = dayRevenue > 0 ? (dayShrinkage / dayRevenue) * 100 : 0;

            gmroiTrendHistory.push({
              date: day.stat_date,
              gmroi: Number(viewData.gmroi) || 0 // Daily GMROI would require more complex calculation
            });
            shrinkageTrendHistory.push({
              date: day.stat_date,
              value: Math.round(shrinkageRate * 100) / 100
            });
          }
        }

        // Calculate trends from daily data
        const gmroiTrend = gmroiTrendHistory.length >= 2
          ? ((gmroiTrendHistory[gmroiTrendHistory.length - 1].gmroi - gmroiTrendHistory[0].gmroi) / (gmroiTrendHistory[0].gmroi || 1)) * 100
          : 0;
        const shrinkageTrend = shrinkageTrendHistory.length >= 2
          ? ((shrinkageTrendHistory[shrinkageTrendHistory.length - 1].value - shrinkageTrendHistory[0].value) / (shrinkageTrendHistory[0].value || 1)) * 100
          : 0;

        // FIX: Prefer GMROI from inventory_quality_metrics (trigger-updated, more reliable)
        // over view_dashboard_kpis (materialized view may be stale)
        // ADR: Dashboard Metrics Audit - Unify GMROI source
        const backendGmroi = qualityMetrics?.gmroi ? Number(qualityMetrics.gmroi) : null;

        const result = {
          gmroi: backendGmroi ?? (Number(viewData.gmroi) || 0),
          gmroiTrend: Math.round(gmroiTrend * 100) / 100,
          shrinkageRate: Number(viewData.shrinkage_rate) || 0,
          shrinkageTrend: Math.round(shrinkageTrend * 100) / 100,
          grossMargin: Math.round(grossMargin),
          avgInventoryCost: Math.round(inventoryCost),
          inventoryTurnover: backendInventoryTurnover ?? (inventoryCost > 0 ? Math.round((totalCOGS / inventoryCost) * 12 * 10) / 10 : 0),
          deadStockValue: backendDeadStockValue !== null ? Math.round(backendDeadStockValue) : Math.round(deadStockValue),
          gmroiByCategory,
          gmroiTrendHistory,
          shrinkageTrendHistory
        };

        if (import.meta.env.DEV) {
          console.debug('[useExtendedFinancialData] Pre-computed results:', {
            gmroi: result.gmroi,
            gmroiSource: backendGmroi !== null ? 'inventory_quality_metrics' : 'view_dashboard_kpis',
            shrinkageRate: result.shrinkageRate,
            grossMargin: result.grossMargin
          });
        }

        return result;
      }

      // ===== FALLBACK: Raw queries if materialized view not available =====
      if (import.meta.env.DEV) console.debug('[useExtendedFinancialData] Falling back to raw transaction queries');

      // Fetch sales transactions for GMROI and turnover calculation
      let salesQuery = supabase
        .from('transactions')
        .select('product_id, quantity, total_amount, products!inner(unit_cost, category)')
        .eq('store_id', storeId)
        .eq('type', 'SALE')
        .gte('created_at', startDate);
      salesQuery = salesQuery.eq('tenant_id', tenantId!);
      const { data: sales, error: salesError } = await salesQuery;

      if (salesError) {
        if (import.meta.env.DEV) console.error('[useExtendedFinancialData] Sales fetch error:', salesError);
      }
      if (import.meta.env.DEV) console.debug(`[useExtendedFinancialData] Sales transactions fetched: ${sales?.length ?? 0}`);

      // Fetch inventory for current stock value
      let invQuery = supabase
        .from('inventory')
        .select('product_id, system_qty, physical_qty, products!inner(unit_cost, unit_price, category)')
        .eq('store_id', storeId);
      invQuery = invQuery.eq('tenant_id', tenantId!);
      const { data: inventory, error: inventoryError } = await invQuery;

      if (inventoryError) {
        if (import.meta.env.DEV) console.error('[useExtendedFinancialData] Inventory fetch error:', inventoryError);
      }
      if (import.meta.env.DEV) console.debug(`[useExtendedFinancialData] Inventory items fetched: ${inventory?.length ?? 0}`);

      // Fetch adjustment transactions for shrinkage
      let adjQuery = supabase
        .from('transactions')
        .select('product_id, quantity, total_amount')
        .eq('store_id', storeId)
        .eq('type', 'ADJUSTMENT')
        .lt('quantity', 0)
        .gte('created_at', startDate);
      adjQuery = adjQuery.eq('tenant_id', tenantId!);
      const { data: adjustments, error: adjustmentsError } = await adjQuery;

      if (adjustmentsError) {
        if (import.meta.env.DEV) console.error('[useExtendedFinancialData] Adjustments fetch error:', adjustmentsError);
      }
      if (import.meta.env.DEV) console.debug(`[useExtendedFinancialData] Adjustments fetched: ${adjustments?.length ?? 0}`);

      // Fetch historical KPIs for trends — SECURITY: defense-in-depth with tenant_id
      let histKpiQuery = supabase
        .from('dashboard_kpis')
        .select('kpi_type, value, calculated_at')
        .eq('store_id', storeId)
        .in('kpi_type', ['shrinkage', 'total_sales'])
        .order('calculated_at', { ascending: true })
        .limit(14);
      histKpiQuery = histKpiQuery.eq('tenant_id', tenantId!);
      const { data: historicalKpis, error: kpisError } = await histKpiQuery;

      if (kpisError) {
        if (import.meta.env.DEV) console.error('[useExtendedFinancialData] Historical KPIs fetch error:', kpisError);
      }
      if (import.meta.env.DEV) console.debug(`[useExtendedFinancialData] Historical KPIs fetched: ${historicalKpis?.length ?? 0}`);

      // Calculate totals
      let totalRevenue = 0;
      let totalCOGS = 0;
      const categoryMetrics: Record<string, { revenue: number; cogs: number; inventoryCost: number }> = {};

      for (const sale of (sales || [])) {
        const unitCost = sale.products?.unit_cost || 0;
        const category = sale.products?.category || 'Other';
        totalRevenue += Number(sale.total_amount) || 0;
        totalCOGS += Math.abs(sale.quantity || 0) * unitCost;

        if (!categoryMetrics[category]) {
          categoryMetrics[category] = { revenue: 0, cogs: 0, inventoryCost: 0 };
        }
        categoryMetrics[category].revenue += Number(sale.total_amount) || 0;
        categoryMetrics[category].cogs += Math.abs(sale.quantity || 0) * unitCost;
      }

      let totalInventoryCost = 0;
      let deadStockValue = 0;
      const productSales = new Set((sales || []).map(s => s.product_id));

      for (const inv of (inventory || [])) {
        const unitCost = inv.products?.unit_cost || 0;
        const category = inv.products?.category || 'Other';
        const qty = inv.physical_qty || inv.system_qty || 0;
        const inventoryValue = qty * unitCost;
        totalInventoryCost += inventoryValue;

        if (!categoryMetrics[category]) {
          categoryMetrics[category] = { revenue: 0, cogs: 0, inventoryCost: 0 };
        }
        categoryMetrics[category].inventoryCost += inventoryValue;

        // Dead stock: items with no sales in the period
        if (!productSales.has(inv.product_id)) {
          deadStockValue += inventoryValue;
        }
      }

      // Calculate shrinkage
      let shrinkageValue = 0;
      for (const adj of (adjustments || [])) {
        shrinkageValue += Number(adj.total_amount) || 0;
      }

      // Calculate KPIs
      const grossMargin = totalRevenue - totalCOGS;
      const gmroi = totalInventoryCost > 0 ? grossMargin / totalInventoryCost : 0;
      const shrinkageRate = totalRevenue > 0 ? (shrinkageValue / totalRevenue) * 100 : 0;
      const inventoryTurnover = totalInventoryCost > 0 ? (totalCOGS / totalInventoryCost) * 12 : 0; // Annualized

      // Calculate GMROI by category
      const gmroiByCategory = Object.entries(categoryMetrics)
        .filter(([_, metrics]) => metrics.inventoryCost > 0 || metrics.revenue > 0)
        .map(([category, metrics]) => ({
          category,
          gmroi: metrics.inventoryCost > 0
            ? Math.round(((metrics.revenue - metrics.cogs) / metrics.inventoryCost) * 100) / 100
            : 0,
          grossMargin: metrics.revenue - metrics.cogs
        }))
        .sort((a, b) => b.gmroi - a.gmroi)
        .slice(0, 6);

      // Build trend histories from historical KPIs
      const gmroiTrendHistory: Array<{ date: string; gmroi: number }> = [];
      const shrinkageTrendHistory: Array<{ date: string; value: number }> = [];

      // Group KPIs by date for trends
      const kpisByDate: Record<string, { shrinkage?: number; sales?: number }> = {};
      for (const kpi of (historicalKpis || [])) {
        const date = new Date(kpi.calculated_at).toISOString().split('T')[0];
        if (!kpisByDate[date]) {
          kpisByDate[date] = {};
        }
        if (kpi.kpi_type === 'shrinkage') {
          kpisByDate[date].shrinkage = Number(kpi.value) || 0;
        } else if (kpi.kpi_type === 'total_sales') {
          kpisByDate[date].sales = Number(kpi.value) || 0;
        }
      }

      // If no historical data, return empty arrays - UI should handle empty state
      if (Object.keys(kpisByDate).length > 0) {
        // Use actual historical data
        const sortedDates = Object.keys(kpisByDate).sort();
        for (const date of sortedDates.slice(-7)) {
          const data = kpisByDate[date];
          const dailyShrinkageRate = data.sales && data.sales > 0
            ? ((data.shrinkage || 0) / data.sales) * 100
            : shrinkageRate;

          gmroiTrendHistory.push({
            date,
            gmroi: gmroi // Would need GMROI history for accurate trend
          });
          shrinkageTrendHistory.push({
            date,
            value: Math.round(dailyShrinkageRate * 100) / 100
          });
        }
      }
      // If no historical data, gmroiTrendHistory and shrinkageTrendHistory remain empty

      // Calculate trends (percentage change from first to last)
      const gmroiTrend = gmroiTrendHistory.length >= 2
        ? ((gmroiTrendHistory[gmroiTrendHistory.length - 1].gmroi - gmroiTrendHistory[0].gmroi) / (gmroiTrendHistory[0].gmroi || 1)) * 100
        : 0;
      const shrinkageTrend = shrinkageTrendHistory.length >= 2
        ? ((shrinkageTrendHistory[shrinkageTrendHistory.length - 1].value - shrinkageTrendHistory[0].value) / (shrinkageTrendHistory[0].value || 1)) * 100
        : 0;

      const result = {
        gmroi: Math.round(gmroi * 100) / 100,
        gmroiTrend: Math.round(gmroiTrend * 100) / 100,
        shrinkageRate: Math.round(shrinkageRate * 100) / 100,
        shrinkageTrend: Math.round(shrinkageTrend * 100) / 100,
        grossMargin: Math.round(grossMargin),
        avgInventoryCost: Math.round(totalInventoryCost),
        inventoryTurnover: backendInventoryTurnover ?? Math.round(inventoryTurnover * 10) / 10,
        deadStockValue: backendDeadStockValue !== null ? Math.round(backendDeadStockValue) : Math.round(deadStockValue),
        gmroiByCategory,
        gmroiTrendHistory,
        shrinkageTrendHistory
      };

      if (import.meta.env.DEV) {
        console.debug('[useExtendedFinancialData] Calculated results:', {
          gmroi: result.gmroi,
          shrinkageRate: result.shrinkageRate,
          grossMargin: result.grossMargin,
          avgInventoryCost: result.avgInventoryCost,
          inventoryTurnover: result.inventoryTurnover,
          categoriesCount: gmroiByCategory.length,
          trendHistoryLength: gmroiTrendHistory.length
        });
      }

      return result;
    },
    staleTime: STALE_TIMES.EXTENDED_FINANCIALS,
    enabled: !!storeId && !!tenantId,
  });
};

// ============================================
// OPERATIONAL KPIs HOOK (v3.5.0)
// ============================================

/**
 * Operational KPIs data from real database sources.
 * Replaces hardcoded fallback values in OperationalDashboard.tsx
 */
export interface OperationalKPIsData {
  // Supply chain KPIs - from purchase_orders table
  stockoutRate: number;         // % of orders with stockouts
  onTimeDeliveryRate: number;   // % of POs received on time
  orderFulfillmentCycleTime: number; // Avg hours from order to receipt
  orderAccuracy: number;        // % of orders received without discrepancy

  // Alert system KPIs - from alerts table
  alertMTTR: number;            // Mean Time To Resolve (hours)
  falsePositiveRate: number;    // % of alerts marked as false positive

  // Inventory KPIs - from inventory_quality_metrics or calculated
  inventoryAccuracy: number;    // IRA %
  inventoryTurnover: number;    // Turns per year
}

/**
 * Fetch operational KPIs using TanStack Query.
 * Sources data from purchase_orders, alerts, and inventory tables.
 * Caching: 5 minute staleTime (operational metrics)
 *
 * @see types.ts OperationalKPIs interface
 * @see supabase/migrations/20260116000002_purchase_order_receiving_schema.sql
 */
export const useOperationalKPIs = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.operationalKPIs(tenantId!, storeId),
    queryFn: async (): Promise<OperationalKPIsData> => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const startDate = thirtyDaysAgo.toISOString();

      // Initialize with zeros (No Mock Data Policy - return 0 when data unavailable)
      const result: OperationalKPIsData = {
        stockoutRate: 0,
        onTimeDeliveryRate: 0,
        orderFulfillmentCycleTime: 0,
        orderAccuracy: 0,
        alertMTTR: 0,
        falsePositiveRate: 0,
        inventoryAccuracy: 0,
        inventoryTurnover: 0,
      };

      // ==========================================
      // 1. SUPPLY CHAIN KPIs from purchase_orders
      // ==========================================
      try {
        // Fetch completed purchase orders for this store
        const { data: purchaseOrders, error: poError } = await supabase
          .from('purchase_orders')
          .select('id, is_late, delay_hours, total_units_ordered, total_units_received, is_short_shipped, short_ship_percentage, order_date, actual_arrival')
          .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
          .eq('store_id', storeId)
          .in('status', ['received', 'partially_received'])
          .gte('actual_arrival', startDate);

        if (!poError && purchaseOrders && purchaseOrders.length > 0) {
          const totalPOs = purchaseOrders.length;

          // On-Time Delivery Rate: % of POs not late
          const onTimePOs = purchaseOrders.filter((po: PurchaseOrderRow) => !po.is_late).length;
          result.onTimeDeliveryRate = Math.round((onTimePOs / totalPOs) * 1000) / 10; // One decimal

          // Order Fulfillment Cycle Time: Average delay_hours (for orders with actual_arrival)
          const posWithCycle = purchaseOrders.filter((po: PurchaseOrderRow) =>
            po.order_date && po.actual_arrival
          );
          if (posWithCycle.length > 0) {
            const totalCycleHours = posWithCycle.reduce((sum: number, po: PurchaseOrderRow) => {
              const orderDate = new Date(po.order_date);
              const arrivalDate = new Date(po.actual_arrival);
              const cycleHours = (arrivalDate.getTime() - orderDate.getTime()) / (1000 * 60 * 60);
              return sum + cycleHours;
            }, 0);
            result.orderFulfillmentCycleTime = Math.round((totalCycleHours / posWithCycle.length) * 10) / 10;
          }

          // Order Accuracy: % of orders without short shipment
          const accurateOrders = purchaseOrders.filter((po: PurchaseOrderRow) => !po.is_short_shipped).length;
          result.orderAccuracy = Math.round((accurateOrders / totalPOs) * 1000) / 10;

          // Stockout Rate: Based on short ship percentage (average)
          const posWithShortage = purchaseOrders.filter((po: PurchaseOrderRow) =>
            po.is_short_shipped && po.short_ship_percentage != null && po.short_ship_percentage > 0
          );
          if (posWithShortage.length > 0) {
            const avgShortage = posWithShortage.reduce((sum: number, po: PurchaseOrderRow) =>
              sum + (Number(po.short_ship_percentage) || 0), 0) / posWithShortage.length;
            result.stockoutRate = Math.round(avgShortage * 10) / 10;
          }

          if (import.meta.env.DEV) console.debug(`[useOperationalKPIs] PO metrics calculated from ${totalPOs} orders`);
        } else {
          if (import.meta.env.DEV) console.debug('[useOperationalKPIs] No purchase orders found for store');
        }
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[useOperationalKPIs] Error fetching purchase orders:', err);
      }

      // ==========================================
      // 2. ALERT SYSTEM KPIs from alerts table
      // ==========================================
      try {
        // Fetch resolved alerts for MTTR calculation
        // SECURITY: Filter by tenant_id for defense-in-depth
        const { data: resolvedAlerts, error: alertError } = await supabase
          .from('alerts')
          .select('id, status, created_at, resolved_at')
          .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
          .eq('store_id', storeId)
          .in('status', ['resolved', 'dismissed', 'false_positive'])
          .gte('resolved_at', startDate);

        if (!alertError && resolvedAlerts && resolvedAlerts.length > 0) {
          // MTTR: Mean Time To Resolve (hours)
          const alertsWithResolution = resolvedAlerts.filter((a: AlertResolutionRow) =>
            a.created_at && a.resolved_at
          );
          if (alertsWithResolution.length > 0) {
            const totalResolutionHours = alertsWithResolution.reduce((sum: number, alert: AlertResolutionRow) => {
              const created = new Date(alert.created_at);
              const resolved = new Date(alert.resolved_at);
              const hours = (resolved.getTime() - created.getTime()) / (1000 * 60 * 60);
              return sum + Math.max(0, hours); // Ensure non-negative
            }, 0);
            result.alertMTTR = Math.round((totalResolutionHours / alertsWithResolution.length) * 10) / 10;
          }

          // False Positive Rate: % of alerts marked as dismissed/false_positive
          const falsePositives = resolvedAlerts.filter((a: AlertResolutionRow) =>
            a.status === 'dismissed' || a.status === 'false_positive'
          ).length;
          result.falsePositiveRate = Math.round((falsePositives / resolvedAlerts.length) * 1000) / 10;

          if (import.meta.env.DEV) console.debug(`[useOperationalKPIs] Alert metrics calculated from ${resolvedAlerts.length} alerts`);
        } else {
          if (import.meta.env.DEV) console.debug('[useOperationalKPIs] No resolved alerts found for store');
        }
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[useOperationalKPIs] Error fetching alerts:', err);
      }

      // ==========================================
      // 3. INVENTORY KPIs from inventory_quality_metrics or view_dashboard_kpis
      // ==========================================
      try {
        // First try inventory_quality_metrics
        // SECURITY: Filter by tenant_id for defense-in-depth
        const { data: qualityMetrics, error: qualityError } = await supabase
          .from('inventory_quality_metrics')
          .select('ira, gmroi')
          .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
          .eq('store_id', storeId)
          .order('calculated_at', { ascending: false })
          .limit(1)
          .single();

        if (!qualityError && qualityMetrics) {
          result.inventoryAccuracy = Number(qualityMetrics.ira) || 0;
          result.inventoryTurnover = Number(qualityMetrics.gmroi) || 0; // gmroi as proxy (no inventory_turnover column)
          if (import.meta.env.DEV) console.debug('[useOperationalKPIs] Inventory metrics from inventory_quality_metrics');
        } else {
          // Fallback to view_dashboard_kpis
          const { data: viewData, error: viewError } = await supabase
            .from('view_dashboard_kpis')
            .select('ira, total_cogs_30d, inventory_cost')
            .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
            .eq('store_id', storeId)
            .single();

          if (!viewError && viewData) {
            result.inventoryAccuracy = Number(viewData.ira) || 0;
            // Calculate turnover: (COGS / Avg Inventory Cost) * 12 months
            const cogs = Number(viewData.total_cogs_30d) || 0;
            const inventoryCost = Number(viewData.inventory_cost) || 0;
            if (inventoryCost > 0) {
              result.inventoryTurnover = Math.round((cogs / inventoryCost) * 12 * 10) / 10;
            }
            if (import.meta.env.DEV) console.debug('[useOperationalKPIs] Inventory metrics from view_dashboard_kpis');
          }
        }
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[useOperationalKPIs] Error fetching inventory metrics:', err);
      }

      if (import.meta.env.DEV) console.debug('[useOperationalKPIs] Final results:', result);
      return result;
    },
    staleTime: STALE_TIMES.KPI, // 5 minutes
    enabled: !!tenantId && !!storeId,  // SECURITY: Don't fetch without tenant context
  });
};

/**
 * Fetch process errors using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 */
export const useProcessErrors = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.processErrors(tenantId!, storeId),
    queryFn: async (): Promise<ProcessError[]> => {
      // Derive process errors from alerts with process-related types
      const processAlertTypes = ['receiving_error', 'stocking_error', 'unrecorded_sales', 'ghost_inventory'];

      // SECURITY: Filter by tenant_id for defense-in-depth
      const { data: alerts, error: alertsError } = await supabase
        .from('alerts')
        .select('id, type, description, status, created_at, metadata')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .in('type', processAlertTypes)
        .order('created_at', { ascending: false })
        .limit(50);

      if (alertsError) {
        if (import.meta.env.DEV) console.error(`[useProcessErrors] Error fetching process alerts (HTTP: ${alertsError.code}):`, alertsError.message);
        return [];
      }

      if (!alerts || alerts.length === 0) {
        if (import.meta.env.DEV) console.debug(`[useProcessErrors] No process errors found for store ${storeId}`);
        return [];
      }

      // Transform alerts into ProcessError format - matching ProcessError interface
      const processErrors: ProcessError[] = alerts.map(alert => {
        const metadata = alert.metadata as Record<string, unknown> | null;
        return {
          id: alert.id,
          type: alert.type as ProcessError['type'],
          location: (metadata?.zone as string) || (metadata?.location as string) || 'Unknown',
          sku: (metadata?.sku as string) || 'UNKNOWN',
          productName: (metadata?.product_name as string) || (metadata?.productName as string) || 'Unknown Product',
          systemQty: (metadata?.system_qty as number) || (metadata?.systemQty as number) || 0,
          actualQty: (metadata?.actual_qty as number) || (metadata?.actualQty as number) || 0,
          probableCause: alert.description || `Process error: ${alert.type}`,
          confidence: (metadata?.confidence as number) || 50,
          timestamp: alert.created_at
        };
      });

      return processErrors;
    },
    staleTime: STALE_TIMES.DEFAULT,
    enabled: !!tenantId && !!storeId,  // SECURITY: Don't fetch without tenant context
  });
};

/**
 * Fetch zone metrics using TanStack Query.
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 */
export const useZoneMetrics = (tenantId: string | undefined, storeId: string, zoneId: string) => {
  return useQuery({
    queryKey: queryKeys.zoneMetrics(tenantId!, storeId, zoneId),
    queryFn: async (): Promise<ZoneProcessMetrics | null> => {
      // Zone metrics come from zone_metrics table
      // SECURITY: Filter by tenant_id for defense-in-depth
      const { data, error } = await supabase
        .from('zone_metrics')
        .select('*')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .eq('zone_id', zoneId)
        .order('measured_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) {
        if (import.meta.env.DEV) console.debug(`[useZoneMetrics] No metrics found for zone ${zoneId}`);
        return null;
      }

      return {
        zoneId: data.zone_id,
        errorRate: data.error_rate || 0,
        mostCommonError: data.most_common_error || '',
        activeErrors: data.active_errors || [],
      };
    },
    staleTime: STALE_TIMES.DEFAULT,
    enabled: !!tenantId && !!storeId && !!zoneId,  // SECURITY: Don't fetch without tenant context
  });
};

// Re-export useAsyncData as useMockApi for backwards compatibility
export const useMockApi = useAsyncData;

// ============================================
// KPI TREND HISTORY HOOK (v3.6.0)
// Replaces mock generateTrend() with real historical data
// ============================================

export interface KpiTrendPoint {
  date: string;
  value: number;
  isSnapshot?: boolean;
}

/**
 * Fetch KPI trend history from daily_store_stats.
 * Returns real historical data for the last 30 days.
 * If no data exists, returns empty array (No Mock Data Policy).
 *
 * @param storeId - The store UUID
 * @param kpiType - Which KPI to fetch ('ira' | 'phantom' | 'gmroi' | 'doh' | 'shrinkage')
 */
/**
 * Interface for ranked items used in metric detail modals
 */
export interface RankedListItem {
  label: string;
  subLabel: string;
  value: string;
  status: 'good' | 'warning' | 'bad' | 'neutral';
}

/**
 * Fetch real metric rankings from database.
 * Replaces mock generateMetricRankings() with actual data.
 *
 * @param storeId - The store UUID
 * @param metric - Which metric to rank ('ira' | 'phantom' | 'gmroi' | 'doh')
 */
export const useMetricRankings = (tenantId: string | undefined, storeId: string, metric: 'ira' | 'phantom' | 'gmroi' | 'doh') => {
  return useQuery({
    queryKey: ['metricRankings', tenantId, storeId, metric],
    queryFn: async (): Promise<{ topList: { title: string; items: RankedListItem[] }; bottomList: { title: string; items: RankedListItem[] } }> => {
      // Fetch inventory with product details
      const { data: inventory, error } = await supabase
        .from('inventory')
        .select(`
          product_id,
          system_qty,
          physical_qty,
          products (
            sku,
            name,
            category,
            unit_cost,
            unit_price
          )
        `)
        .eq('tenant_id', tenantId!)
        .eq('store_id', storeId)
        .gt('system_qty', 0);

      if (error || !inventory || inventory.length === 0) {
        if (import.meta.env.DEV) console.debug(`[useMetricRankings] No data for ${metric} - returning empty lists`);
        return {
          topList: { title: 'Mejores', items: [] },
          bottomList: { title: 'Peores', items: [] }
        };
      }

      // Fetch recent sales for DOH/GMROI calculations
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: salesData } = await supabase
        .from('transactions')
        .select('product_id, quantity, total_amount')
        .eq('tenant_id', tenantId!)
        .eq('store_id', storeId)
        .eq('type', 'SALE')
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Build sales map
      const productSales: Record<string, { qty: number; revenue: number }> = {};
      for (const sale of (salesData || [])) {
        if (!productSales[sale.product_id]) {
          productSales[sale.product_id] = { qty: 0, revenue: 0 };
        }
        productSales[sale.product_id].qty += Math.abs(sale.quantity || 0);
        productSales[sale.product_id].revenue += Number(sale.total_amount) || 0;
      }

      // Calculate metrics for each product
      const items = inventory.map((inv: InventoryProductRow) => {
        const product = inv.products;
        if (!product) return null;

        const systemQty = inv.system_qty || 0;
        const physicalQty = inv.physical_qty || 0;
        const unitCost = product.unit_cost || 0;
        const unitPrice = product.unit_price || 0;
        const sales = productSales[inv.product_id] || { qty: 0, revenue: 0 };

        // Calculate different metrics
        const accuracy = systemQty > 0 ? (physicalQty / systemQty) * 100 : 100;
        const avgDailySales = sales.qty / 30;
        const doh = avgDailySales > 0 ? Math.round(systemQty / avgDailySales) : 999;
        const inventoryCost = systemQty * unitCost;
        const grossMargin = sales.revenue - (sales.qty * unitCost);
        const gmroi = inventoryCost > 0 ? Math.round((grossMargin / inventoryCost) * 100) / 100 : 0;

        return {
          label: product.name,
          subLabel: product.category || 'General',
          ira: Math.round(accuracy),
          doh,
          gmroi,
          phantom: systemQty > 0 && sales.qty === 0 ? 1 : 0 // Binary: is phantom or not
        };
      }).filter((item): item is NonNullable<typeof item> => item !== null);

      // Sort and format based on metric type
      let topItems: RankedListItem[] = [];
      let bottomItems: RankedListItem[] = [];

      switch (metric) {
        case 'ira':
          items.sort((a, b) => b.ira - a.ira);
          topItems = items.slice(0, 5).map(i => ({
            label: i.label,
            subLabel: i.subLabel,
            value: `${i.ira}%`,
            status: i.ira >= 98 ? 'good' : i.ira >= 90 ? 'warning' : 'bad'
          }));
          bottomItems = items.slice(-5).reverse().map(i => ({
            label: i.label,
            subLabel: i.subLabel,
            value: `${i.ira}%`,
            status: i.ira >= 98 ? 'good' : i.ira >= 90 ? 'warning' : 'bad'
          }));
          break;

        case 'gmroi':
          items.sort((a, b) => b.gmroi - a.gmroi);
          topItems = items.slice(0, 5).map(i => ({
            label: i.label,
            subLabel: i.subLabel,
            value: fmtScore(i.gmroi, 2),
            status: i.gmroi >= 2.5 ? 'good' : i.gmroi >= 1.5 ? 'warning' : 'bad'
          }));
          bottomItems = items.slice(-5).reverse().map(i => ({
            label: i.label,
            subLabel: i.subLabel,
            value: fmtScore(i.gmroi, 2),
            status: i.gmroi >= 2.5 ? 'good' : i.gmroi >= 1.5 ? 'warning' : 'bad'
          }));
          break;

        case 'doh':
          items.sort((a, b) => a.doh - b.doh); // Lower DOH is better
          topItems = items.slice(0, 5).map(i => ({
            label: i.label,
            subLabel: i.subLabel,
            value: `${i.doh} días`,
            status: i.doh < 45 ? 'good' : i.doh < 70 ? 'warning' : 'bad'
          }));
          bottomItems = items.slice(-5).reverse().map(i => ({
            label: i.label,
            subLabel: i.subLabel,
            value: `${i.doh} días`,
            status: i.doh < 45 ? 'good' : i.doh < 70 ? 'warning' : 'bad'
          }));
          break;

        case 'phantom':
          // For phantom, top = worst (highest phantom count), bottom = best
          const phantomItems = items.filter(i => i.phantom === 1);
          topItems = phantomItems.slice(0, 5).map(i => ({
            label: i.label,
            subLabel: i.subLabel,
            value: `${i.doh}+ días sin venta`,
            status: 'bad'
          }));
          const nonPhantom = items.filter(i => i.phantom === 0);
          bottomItems = nonPhantom.slice(0, 5).map(i => ({
            label: i.label,
            subLabel: i.subLabel,
            value: 'Ventas activas',
            status: 'good'
          }));
          break;
      }

      return {
        topList: { title: 'Mejores', items: topItems },
        bottomList: { title: 'Peores', items: bottomItems }
      };
    },
    staleTime: STALE_TIMES.KPI_DETAILS,
    enabled: !!tenantId && !!storeId,
  });
};

export const useKpiTrendHistory = (storeId: string, kpiType: 'ira' | 'phantom' | 'gmroi' | 'doh' | 'shrinkage', days: number = 30, tenantId?: string) => {
  return useQuery({
    queryKey: ['kpiTrendHistory', storeId, kpiType, days, tenantId],
    queryFn: async (): Promise<KpiTrendPoint[]> => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Tier 0: For IRA, phantom, DOH — query inventory_snapshots first (requires snapshots)
      if ((kpiType === 'ira' || kpiType === 'phantom' || kpiType === 'doh') && tenantId) {
        const snapshotColumn: Record<string, string> = {
          ira: 'ira',
          phantom: 'phantom_rate',
          doh: 'doh',
        };

        const { data: snapshots, error: snapError } = await supabase
          .from('inventory_snapshots')
          .select(`snapshot_date, ${snapshotColumn[kpiType]}`)
          .eq('tenant_id', tenantId)
          .eq('store_id', storeId)
          .gte('snapshot_date', startDate.toISOString().split('T')[0])
          .order('snapshot_date', { ascending: true });

        if (!snapError && snapshots && snapshots.length > 0) {
          // Deduplicate by date (take latest snapshot per day)
          const byDate = new Map<string, number>();
          for (const row of snapshots) {
            const val = Number(row[snapshotColumn[kpiType]]);
            if (val !== null && !isNaN(val)) {
              byDate.set(row.snapshot_date, val);
            }
          }

          if (byDate.size > 0) {
            return Array.from(byDate.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([dateStr, value]) => ({
                date: new Date(dateStr).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }),
                value: Math.round(value * 100) / 100,
                isSnapshot: true,
              }));
          }
        }
      }

      // First try: Get from inventory_quality_metrics if it has history
      // This table stores calculated KPIs per store per day
      let qualityQuery = supabase
        .from('inventory_quality_metrics')
        .select('calculated_at, ira, phantom_rate, gmroi, doh, shrinkage_rate')
        .eq('store_id', storeId);
      if (tenantId) qualityQuery = qualityQuery.eq('tenant_id', tenantId);
      const { data: qualityHistory, error: qualityError } = await qualityQuery
        .gte('calculated_at', startDate.toISOString())
        .order('calculated_at', { ascending: true });

      if (!qualityError && qualityHistory && qualityHistory.length > 0) {
        // Map the requested KPI type to the column
        const kpiColumn: Record<string, string> = {
          ira: 'ira',
          phantom: 'phantom_rate',
          gmroi: 'gmroi',
          doh: 'doh',
          shrinkage: 'shrinkage_rate'
        };

        return qualityHistory.map((row: QualityMetricsRow) => ({
          date: new Date(row.calculated_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }),
          value: Number(row[kpiColumn[kpiType]]) || 0
        }));
      }

      // Fallback: Calculate from daily_store_stats
      let statsQuery = supabase
        .from('daily_store_stats')
        .select('stat_date, total_revenue, total_cogs, gross_margin, shrinkage_value, units_sold')
        .eq('store_id', storeId);
      if (tenantId) statsQuery = statsQuery.eq('tenant_id', tenantId);
      const { data: dailyStats, error: statsError } = await statsQuery
        .gte('stat_date', startDate.toISOString().split('T')[0])
        .order('stat_date', { ascending: true });

      if (!statsError && dailyStats && dailyStats.length > 0) {
        // Calculate KPI values from daily stats
        const points = dailyStats.map((day: DailyStatRow) => {
          let value = 0;
          switch (kpiType) {
            case 'shrinkage':
              value = day.total_revenue > 0
                ? (day.shrinkage_value / day.total_revenue) * 100
                : 0;
              break;
            case 'gmroi':
              value = day.total_cogs > 0
                ? day.gross_margin / day.total_cogs
                : 0;
              break;
            default:
              value = 0;
          }
          return {
            date: new Date(day.stat_date).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }),
            value: Math.round(value * 100) / 100
          };
        });
        if (points.length > 0) return points;
      }

      // Third fallback: Compute from raw transactions + products (always available)
      if (kpiType === 'shrinkage' || kpiType === 'gmroi') {
        const { data: txns, error: txnError } = await supabase
          .from('transactions')
          .select('type, quantity, total_amount, created_at, products!inner(unit_cost)')
          .eq('store_id', storeId)
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true });

        if (!txnError && txns && txns.length > 0) {
          // Group transactions by date
          const byDate = new Map<string, { revenue: number; cogs: number; margin: number; shrinkage: number }>();
          for (const t of txns) {
            const dateKey = new Date(t.created_at).toISOString().split('T')[0];
            if (!byDate.has(dateKey)) {
              byDate.set(dateKey, { revenue: 0, cogs: 0, margin: 0, shrinkage: 0 });
            }
            const day = byDate.get(dateKey)!;
            const unitCost = Number(t.products?.unit_cost) || 0;
            const qty = Math.abs(t.quantity);
            if (t.type === 'SALE') {
              const amount = Number(t.total_amount) || 0;
              day.revenue += amount;
              day.cogs += qty * unitCost;
              day.margin += amount - qty * unitCost;
            } else if (t.type === 'ADJUSTMENT' && t.quantity < 0) {
              day.shrinkage += Number(t.total_amount) || qty * unitCost;
            }
          }

          const sorted = Array.from(byDate.entries()).sort(([a], [b]) => a.localeCompare(b));
          return sorted.map(([dateStr, day]) => {
            let value = 0;
            if (kpiType === 'shrinkage') {
              value = day.revenue > 0 ? (day.shrinkage / day.revenue) * 100 : 0;
            } else {
              value = day.cogs > 0 ? day.margin / day.cogs : 0;
            }
            return {
              date: new Date(dateStr).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }),
              value: Math.round(value * 100) / 100,
            };
          });
        }
      }

      // For IRA, phantom, DOH — no raw transaction fallback possible (requires inventory snapshots)
      if (import.meta.env.DEV) console.debug(`[useKpiTrendHistory] No historical data for ${kpiType} - returning empty array`);
      return []; // No Mock Data Policy - return empty array
    },
    staleTime: STALE_TIMES.KPI_DETAILS, // 5 minutes
    enabled: !!storeId,
  });
};

// ============================================
// TENANT USER HOOKS (For Tenant-Isolated User Lists)
// ============================================

export interface TenantUser {
  id: string;
  fullName: string | null;
  email: string;
  role: string;
}

/**
 * Fetch users belonging to the current tenant using TanStack Query.
 * This hook is tenant-isolated via RLS policies and ensures
 * user dropdowns only show users from the same tenant.
 */
export const useTenantUsers = () => {
  const { tenant } = useAuth();
  const tenantId = tenant?.id;
  return useQuery({
    queryKey: queryKeys.tenantUsers(tenantId ?? ''),
    queryFn: async (): Promise<TenantUser[]> => {
      // SECURITY: explicit tenant filter — RLS alone lets super admins see every tenant's users
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, email, role')
        .eq('tenant_id', tenantId!)
        .order('full_name', { ascending: true });

      if (error) {
        if (import.meta.env.DEV) console.warn('Failed to fetch tenant users:', error);
        return [];
      }

      return (data || []).map(user => ({
        id: user.id,
        fullName: user.full_name,
        email: user.email,
        role: user.role,
      }));
    },
    enabled: !!tenantId,
    staleTime: STALE_TIMES.DEFAULT,
  });
};

// ============================================
// PRODUCT ANALYSIS HOOKS (For Scatter Plot Drill-Down)
// ============================================

export interface ProductHistoryPoint {
  date: string;
  stock: number;
  sales: number;
}

export interface ProductTransactionRecord {
  id: string;
  date: string;
  type: 'SALE' | 'RECEIPT' | 'ADJUSTMENT' | 'RETURN';
  quantity: number;
  user: string;
}

/**
 * Fetch product analysis data for drill-down views from scatter plot clicks.
 *
 * Returns:
 * - history: 14-day stock movement and sales data
 * - transactions: Last 5 transactions for the product
 *
 * NO MOCK DATA: Returns empty arrays if data unavailable.
 */
export const useProductAnalysis = (tenantId: string | undefined, storeId: string, productSku: string | null) => {
  return useQuery({
    queryKey: ['productAnalysis', tenantId, storeId, productSku],
    queryFn: async (): Promise<{ history: ProductHistoryPoint[]; transactions: ProductTransactionRecord[] }> => {
      if (!productSku) {
        return { history: [], transactions: [] };
      }

      // First, find the product_id from SKU
      const { data: product } = await supabase
        .from('products')
        .select('id')
        .eq('tenant_id', tenantId!)
        .eq('sku', productSku)
        .single();

      if (!product) {
        if (import.meta.env.DEV) console.warn(`[useProductAnalysis] Product not found for SKU: ${productSku}`);
        return { history: [], transactions: [] };
      }

      const productId = product.id;

      // Fetch transactions for the last 14 days
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      const isoDate = fourteenDaysAgo.toISOString();

      const { data: txData, error: txError } = await supabase
        .from('transactions')
        .select(`
          id,
          created_at,
          type,
          quantity,
          cashier_id,
          users:cashier_id (full_name)
        `)
        .eq('tenant_id', tenantId!)
        .eq('store_id', storeId)
        .eq('product_id', productId)
        .gte('created_at', isoDate)
        .order('created_at', { ascending: false });

      if (txError) {
        if (import.meta.env.DEV) console.warn('[useProductAnalysis] Failed to fetch transactions:', txError);
      }

      // Build transactions array (last 5)
      const transactions: ProductTransactionRecord[] = (txData || []).slice(0, 5).map((tx: TransactionRow) => ({
        id: tx.id,
        date: tx.created_at ? new Date(tx.created_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
        type: mapTransactionType(tx.type),
        quantity: tx.quantity,
        user: tx.users?.full_name || tx.cashier_id || 'Sistema',
      }));

      // Build 14-day history by aggregating transactions by day
      const history = buildHistoryFromTransactions(txData || [], storeId, productId);

      return { history, transactions };
    },
    staleTime: STALE_TIMES.KPI_DETAILS, // 5 minutes
    enabled: !!tenantId && !!storeId && !!productSku,
  });
};

/**
 * Map database transaction type to UI type
 */
function mapTransactionType(dbType: string | null): 'SALE' | 'RECEIPT' | 'ADJUSTMENT' | 'RETURN' {
  switch (dbType?.toLowerCase()) {
    case 'sale':
    case 'venta':
      return 'SALE';
    case 'receipt':
    case 'ingreso':
      return 'RECEIPT';
    case 'return':
    case 'devolucion':
      return 'RETURN';
    case 'adjustment':
    case 'ajuste':
      return 'ADJUSTMENT';
    default:
      return 'ADJUSTMENT';
  }
}

/**
 * Build daily history from transaction data
 * NO MOCK DATA: If no transactions exist, returns empty array
 */
function buildHistoryFromTransactions(
  transactions: TransactionRow[],
  _storeId: string,
  _productId: string
): ProductHistoryPoint[] {
  if (!transactions || transactions.length === 0) {
    return []; // No mock data - return empty if no real data
  }

  // Group transactions by day
  const dailyData = new Map<string, { sales: number; receipts: number }>();

  // Initialize last 14 days
  for (let i = 13; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateKey = date.toISOString().split('T')[0];
    dailyData.set(dateKey, { sales: 0, receipts: 0 });
  }

  // Aggregate transactions
  transactions.forEach((tx: TransactionRow) => {
    if (!tx.created_at) return;
    const dateKey = tx.created_at.split('T')[0];
    const existing = dailyData.get(dateKey);
    if (existing) {
      const type = tx.type?.toLowerCase();
      if (type === 'sale' || type === 'venta') {
        existing.sales += Math.abs(tx.quantity);
      } else if (type === 'receipt' || type === 'ingreso') {
        existing.receipts += Math.abs(tx.quantity);
      }
    }
  });

  // Convert to array - calculate running stock
  // Note: We don't have historical stock snapshots, so we'll show daily sales only
  const history: ProductHistoryPoint[] = [];
  let runningStock = 0;

  // Calculate running stock backwards from most recent
  dailyData.forEach((data, _dateKey) => {
    runningStock += data.receipts - data.sales;
  });

  // Now build forward with actual dates
  dailyData.forEach((data, dateKey) => {
    runningStock = runningStock - data.receipts + data.sales; // Reverse the change
    history.push({
      date: new Date(dateKey).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }),
      stock: Math.max(0, runningStock + data.receipts - data.sales), // Approximate stock level
      sales: data.sales,
    });
    runningStock = runningStock + data.receipts - data.sales; // Apply the change
  });

  return history;
}

// ============================================
// DATA FRESHNESS HOOK (v3.7.0)
// Shows real DB timestamps for data staleness indicators
// ============================================

export { type DataFreshness } from '@/components/common/DataFreshnessBadge';

/**
 * Fetch the most recent timestamps from key data sources.
 * Used by DataFreshnessBadge/DataFreshnessStrip to show data staleness.
 * 4 lightweight queries (1 row each) run in parallel.
 *
 * SECURITY: tenantId is required to prevent cross-tenant data leakage.
 */
export const useDataFreshness = (tenantId: string | undefined, storeId: string) => {
  return useQuery({
    queryKey: queryKeys.dataFreshness(tenantId!, storeId),
    queryFn: async () => {
      const [txResult, statResult, snapResult, qualityResult] = await Promise.all([
        supabase
          .from('transactions')
          .select('created_at')
          .eq('tenant_id', tenantId!)
          .eq('store_id', storeId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('daily_store_stats')
          .select('stat_date')
          .eq('tenant_id', tenantId!)
          .eq('store_id', storeId)
          .order('stat_date', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('inventory_snapshots')
          .select('snapshot_date')
          .eq('tenant_id', tenantId!)
          .eq('store_id', storeId)
          .order('snapshot_date', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('inventory_quality_metrics')
          .select('calculated_at')
          .eq('tenant_id', tenantId!)
          .eq('store_id', storeId)
          .order('calculated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      return {
        lastTransaction: txResult.data?.created_at ?? null,
        lastDailyStat: statResult.data?.stat_date ?? null,
        lastSnapshot: snapResult.data?.snapshot_date ?? null,
        lastQualityCalc: qualityResult.data?.calculated_at ?? null,
      };
    },
    staleTime: STALE_TIMES.KPI,
    enabled: !!tenantId && !!storeId,
  });
};
